import classNames from 'classnames';
import {
  CheckCircleIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import { Trans, useTranslation } from 'react-i18next';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import RetentionOfferShell from '../RetentionOfferShell.component';
import { tPlanNotSuitableOfferModalProps } from '../RetentionOfferModal.types';

const BENEFITS = ['benefit1', 'benefit2', 'benefit3', 'benefit4'] as const;

/**
 * The downgrade offer: Basic instead of leaving, with extra days for the
 * transition. The offer itself carries no price, so the Basic plan's catalog
 * price is shown once the catalog has loaded — never guessed.
 */
export default function PlanNotSuitableOfferModal(
  props: tPlanNotSuitableOfferModalProps,
) {
  const {
    isOpen,
    basicPlanPrice,
    basicPlanCurrency,
    onAcceptOffer,
    onFinalizeSubscription,
    onBack,
    onClose,
    isLoadingOfferAcceptance,
    isLoadingFinalization,
  } = props;
  const { t } = useTranslation('settings/SubscriptionManager');
  const hasPrice = basicPlanPrice !== null;

  return (
    <RetentionOfferShell
      isOpen={isOpen}
      heading={t('cancelSubscription.retentionOfferPlan.heading')}
      intro={t('cancelSubscription.retentionOfferPlan.intro')}
      cardIcon={RectangleGroupIcon}
      cardTitle={t('cancelSubscription.retentionOfferPlan.cardTitle')}
      cardTerm={t('cancelSubscription.retentionOfferPlan.transitionOffer')}
      onBack={onBack}
      onDecline={onFinalizeSubscription}
      onAccept={onAcceptOffer}
      acceptLabel={t('cancelSubscription.retentionOfferPlan.acceptButton')}
      declineLabel={t('cancelSubscription.retentionOfferPlan.declineButton')}
      onClose={onClose}
      isLoadingAccept={isLoadingOfferAcceptance}
      isLoadingDecline={isLoadingFinalization}
      testIdPrefix="plan-not-suitable-offer"
    >
      {hasPrice && (
        <p
          className="text-neutral-800"
          data-testid="plan-not-suitable-offer-price"
        >
          <span className="text-3xl font-bold">
            {formatPrice({
              price: basicPlanPrice,
              currency: basicPlanCurrency,
            })}
            /{t('page.SubscriptionPlans.perMonthly')}
          </span>
        </p>
      )}

      <ul
        className={classNames('flex flex-col gap-2', {
          'border-t border-neutral-200 pt-4': hasPrice,
        })}
      >
        {BENEFITS.map((key) => (
          <li
            key={key}
            className="flex items-start gap-2 text-base text-neutral-700"
          >
            <CheckCircleIcon
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-success"
            />
            <span>
              <Trans
                i18nKey={`settings/SubscriptionManager:cancelSubscription.retentionOfferPlan.${key}`}
                components={{ bold: <strong /> }}
              />
            </span>
          </li>
        ))}
      </ul>
    </RetentionOfferShell>
  );
}
